
import React, { useContext, useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Link, useNavigate } from 'react-router'

// evertel
import { useService } from '@evertel/di'
import { Col, Row, useUI, Text, InfoBox, Icon, MultilinePlaceholder, FormInput } from '@evertel/web/ui'
import { SessionService } from '@evertel/session/SessionService'
import { DrawerNavItemLink } from '../elements'
import { CloneSchemaController, DepartmentDocumentSchemasController, DepartmentSchemaCategoriesController } from '@evertel/document'
import { APIDataDocumentSchema, APIDataDocumentSchemaCategory } from '@evertel/types'
import { DocumentCategoryOption } from './DocumentCategoryOption'
import { DrawerNavigationContext } from '../DrawerNavigationContext'
import { CurrentUserController } from '@evertel/blue-user'

interface DrawerProps {
    tag?: string
}


const DocumentsDrawer: React.FC<DrawerProps> = observer(() => {
    const sessionService = useService(SessionService, [])
    const departmentSchemaCategoriesController = useService(DepartmentSchemaCategoriesController, [])
    const departmentDocumentSchemasController = useService(DepartmentDocumentSchemasController, [])
    const currentUserController = useService(CurrentUserController, [])
    const cloneController = useService(CloneSchemaController, [])

    const navigate = useNavigate()
    const { addToast, toggleLeftNav } = useUI()
    const { updateNavList } = useContext(DrawerNavigationContext)

    const [isCategoryVisible, setIsCategoryVisible] = useState<(string|undefined)[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [filterValue, setFilterValue] = useState('')

    useEffect(() => {
        (async () => {
            if (sessionService?.selectedDepartmentId) {
                setIsLoading(true)

                await departmentSchemaCategoriesController.init(sessionService.selectedDepartmentId)
                await departmentDocumentSchemasController.init(sessionService.selectedDepartmentId)

                setIsLoading(false)
                updateNavList() // Signal navigation can be built

                // set all categories to 'open'
                // const catNames = controller.categories?.map((cat: APIDataDocumentSchemaCategory) => cat.name)
                // setIsCategoryVisible(catNames)
            }
        })()
    }, [sessionService?.selectedDepartmentId, sessionService.currentUserId, departmentSchemaCategoriesController, departmentDocumentSchemasController])


    const onCloneSchema = async (schema: APIDataDocumentSchema) => {
        if (!schema?.id || !sessionService?.selectedDepartmentId) return

        const newName = schema.name + ' (copy)'

        try {
            const newSchema = await cloneController.cloneSchema(schema.id, sessionService?.selectedDepartmentId, newName)

            addToast({
                color: 'success',
                title: 'Your template has been cloned',
                message: 'Edit and enable this template when ready!'
            })

            toggleLeftNav('close')
            navigate(`/everdocs/schema/edit/${newSchema.id}`)

        } catch (error: any) {
            addToast({
                color: 'danger',
                title: 'Clone Unsuccessful',
                message: error.message || error
            })
        }
    }

    const onCreateDoc = () => {
        toggleLeftNav('close')
    }


    if (isLoading) {
        return (
            <MultilinePlaceholder sections={3} />
        )
    }

    if (currentUserController.hasGuestRoleInSelectedDepartment) {
        return null
    }

    const canEditTemplates = currentUserController.canManage


    return (
        <>
            {(canEditTemplates) && (
                <>
                    <DrawerNavItemLink
                        label="Manage Template Categories"
                        icon={{name: 'folder'}}
                        to="/everdocs/schema/categories"
                    />
                    <DrawerNavItemLink
                        label="Create a New Template"
                        icon={{name: 'plus-circle'}}
                        to="/everdocs/schema/add"
                    />
                    <DrawerNavItemLink
                        label="Browse Template Catalog"
                        icon={{name: 'rectangle-history'}}
                        to="/everdocs/schema/templates"
                        className="mb-4"
                    />
                </>
            )}

            <div className="position-relative">
                <FormInput
                    style={{width: '90%'}}
                    type="text"
                    name="filterTemplates"
                    label="Find a template"
                    placeholder="Search..."
                    onChange={(e) => setFilterValue(e.target.value)}
                    value={filterValue}
                    aria-label="Find a template"
                    autoComplete="false"
                    data-drawer-nav="item"
                    inputClassName={filterValue ? 'pe-5' : ''}
                />
                {filterValue && (
                    <button
                        type="button"
                        onClick={() => setFilterValue('')}
                        className="btn btn-close position-absolute p-0"
                        style={{
                            right: 10,
                            bottom: 18,
                            zIndex: 10
                        }}
                        aria-label="Clear filter"
                    />
                )}
            </div>

            {/* ALL */}
            <DocumentCategoryOption
                schemas={departmentDocumentSchemasController.enabledDocumentSchemas}
                isVisible={false}
                onClick={onCreateDoc}
                onCloneSchema={onCloneSchema}
                canManage={canEditTemplates}
                filter={filterValue}
            />

            {departmentSchemaCategoriesController.categories?.map((category: APIDataDocumentSchemaCategory, index) =>
                <DocumentCategoryOption
                    key={`${category.id}-${index}`}
                    category={category}
                    schemas={departmentDocumentSchemasController.schemasForCategory(category.id as number)}
                    isVisible={isCategoryVisible.includes(category.name as string) || !!filterValue.length}
                    onClick={onCreateDoc}
                    onCloneSchema={onCloneSchema}
                    canManage={canEditTemplates}
                    filter={filterValue}
                />
            )}

            {(!!departmentDocumentSchemasController.uncategorizedDocumentSchemas.length) && (
                <DocumentCategoryOption
                    title="Uncategorized"
                    schemas={departmentDocumentSchemasController.uncategorizedDocumentSchemas}
                    isVisible={false || !!filterValue.length}
                    onClick={onCreateDoc}
                    onCloneSchema={onCloneSchema}
                    canManage={canEditTemplates}
                    filter={filterValue}
                />
            )}

            {(canEditTemplates) && (
                <DocumentCategoryOption
                    title="Disabled Templates"
                    schemas={departmentDocumentSchemasController.disabledDocumentSchemas}
                    isVisible={false || !!filterValue.length}
                    onCloneSchema={onCloneSchema}
                    canManage={canEditTemplates}
                    filter={filterValue}
                />
            )}

            {(canEditTemplates) && (
                <Link
                    className="mt-5 link"
                    to={'/everdocs/schema/templates'}
                    data-drawer-nav="item"
                >
                    <InfoBox className="mb-0" color="info">
                        <Row>
                            <Icon
                                name="rectangle-history"
                                className="me-2"
                                size={25}
                            />
                            <Text
                                bold
                                color="blue">
                                Template Catalog
                            </Text>
                            <Icon
                                name="chevron-double-right"
                                className="ms-2"
                                size={11}
                            />
                        </Row>
                        <Text>
                            Browse our catalog of pre-made EverDoc templates!
                        </Text>
                    </InfoBox>
                </Link>
            )}
        </>
    )
})

export { DocumentsDrawer }

